import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';

const EmployerSubscriptionPlans = () => {
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingEmployer, setEditingEmployer] = useState(null);
  const [subscriptionPlan, setSubscriptionPlan] = useState('platinum');
  const [planLimit, setPlanLimit] = useState(30);
  const [updating, setUpdating] = useState(false);
  const pageSize = 20;

  useEffect(() => {
    fetchEmployers();
  }, [currentPage, searchTerm]);

  const fetchEmployers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getEmployers(currentPage, pageSize, searchTerm);
      const employersList = response.data?.employers || response.data || [];
      // Set default plan to platinum if not set, and default plan limit
      const employersWithPlans = employersList.map(emp => {
        const plan = emp.subscriptionPlan || 'platinum';
        const defaultLimits = { silver: 10, gold: 20, platinum: 30 };
        return {
          ...emp,
          subscriptionPlan: plan,
          planLimit: emp.planLimit || defaultLimits[plan.toLowerCase()] || 30
        };
      });
      setEmployers(employersWithPlans);
      setTotalPages(response.data?.totalPages || 1);
    } catch (error) {
      console.error('Error fetching employers:', error);
      toast.error('Failed to load employers');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (employer) => {
    setEditingEmployer(employer);
    setSubscriptionPlan(employer.subscriptionPlan || 'platinum');
    setPlanLimit(employer.planLimit || 30);
  };

  const handleCancelEdit = () => {
    setEditingEmployer(null);
    setSubscriptionPlan('platinum');
    setPlanLimit(30);
  };

  const handleUpdatePlan = async () => {
    if (!editingEmployer) return;

    const limitNum = parseInt(planLimit);
    if (isNaN(limitNum) || limitNum < 0) {
      toast.error('Please enter a valid plan limit');
      return;
    }

    setUpdating(true);
    try {
      await adminAPI.updateEmployerSubscriptionPlan(editingEmployer.id, {
        subscriptionPlan: subscriptionPlan,
        planLimit: limitNum
      });
      toast.success('Subscription plan and limit updated successfully');
      setEditingEmployer(null);
      fetchEmployers();
    } catch (error) {
      console.error('Error updating subscription plan:', error);
      toast.error('Failed to update subscription plan');
    } finally {
      setUpdating(false);
    }
  };

  const getPlanBadgeColor = (plan) => {
    const planLower = (plan || 'platinum').toLowerCase();
    switch (planLower) {
      case 'silver':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'gold':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'platinum':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (loading && employers.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Employer Subscription Plans</h2>
          <p className="text-sm text-gray-600 mt-1">Manage subscription plans and limits for employers</p>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search employers by name, email, or company..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <svg
            className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Employers Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan Limit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {employers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No employers found
                  </td>
                </tr>
              ) : (
                employers.map((employer) => (
                  <tr key={employer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{employer.companyName}</div>
                      {employer.industry && (
                        <div className="text-sm text-gray-500">{employer.industry}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {employer.firstName} {employer.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{employer.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingEmployer?.id === employer.id ? (
                        <select
                          value={subscriptionPlan}
                          onChange={(e) => setSubscriptionPlan(e.target.value)}
                          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="silver">Silver</option>
                          <option value="gold">Gold</option>
                          <option value="platinum">Platinum</option>
                        </select>
                      ) : (
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getPlanBadgeColor(employer.subscriptionPlan)}`}>
                          {(employer.subscriptionPlan || 'platinum').charAt(0).toUpperCase() + (employer.subscriptionPlan || 'platinum').slice(1)}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingEmployer?.id === employer.id ? (
                        <input
                          type="number"
                          value={planLimit}
                          onChange={(e) => setPlanLimit(parseInt(e.target.value) || 0)}
                          className="w-20 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          min="0"
                        />
                      ) : (
                        <span className="text-sm text-gray-900">{employer.planLimit || 30}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          employer.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {employer.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {employer.isVerified && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Verified
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {editingEmployer?.id === employer.id ? (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={handleUpdatePlan}
                            disabled={updating}
                            className="text-primary-600 hover:text-primary-900 disabled:opacity-50"
                          >
                            {updating ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={updating}
                            className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEdit(employer)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployerSubscriptionPlans;

